import { ColorEnum } from '@/constants/color'
import { ShiftCloseoutPreviewData } from '@/features/shiftCloseouts/types'
import CashIcon from '@/features/ui/icons/components/CashIcon'
import Typography from '@/features/ui/typography'
import { formatPrice } from '@/utils/priceHelper'
import { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { StyleSheet, View } from 'react-native'

interface HandoverProps {
    handover: ShiftCloseoutPreviewData['handover']
}

const Handover: FC<HandoverProps> = ({ handover }) => {
    const { t } = useTranslation()
    const breakdown = [handover.cash, handover.mealVouchers]
        .map(({ amount, currency }) => formatPrice(amount, currency))
        .join(' + ')

    return (
        <View style={styles.section} testID="shift-closeout-handover">
            <Typography.Heading variant="h5" style={styles.sectionHeading}>
                {t('shiftCloseouts.handover.heading')}
            </Typography.Heading>

            <View style={styles.handover}>
                <View style={styles.info}>
                    <View style={styles.icon}>
                        <CashIcon fill={ColorEnum.YELLOW_600} height={24} width={24} />
                    </View>
                    <View style={styles.text}>
                        <Typography.Description font="bold" size="tiny" style={styles.label}>
                            {t('shiftCloseouts.handover.label').toUpperCase()}
                        </Typography.Description>
                        {breakdown && (
                            <Typography.Description
                                font="bold"
                                size="tiny"
                                style={styles.breakdown}
                            >
                                {breakdown}
                            </Typography.Description>
                        )}
                    </View>
                </View>

                <Typography.Heading variant="h3" numberOfLines={1} style={styles.total}>
                    {formatPrice(handover.total.amount, handover.total.currency)}
                </Typography.Heading>
            </View>
        </View>
    )
}

const styles = StyleSheet.create({
    section: {
        gap: 8,
    },
    sectionHeading: {
        color: ColorEnum.GRAY_900,
    },
    handover: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: 8,
        borderRadius: 12,
        backgroundColor: ColorEnum.Yellow100,
    },
    info: {
        flex: 1,
        minWidth: 0,
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    icon: {
        height: 40,
        width: 40,
        flexShrink: 0,
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 8,
        backgroundColor: ColorEnum.YELLOW_200,
    },
    text: {
        flex: 1,
        minWidth: 0,
        gap: 2,
    },
    label: {
        flexShrink: 1,
        color: ColorEnum.JET_500,
        letterSpacing: 0.5,
    },
    breakdown: {
        color: ColorEnum.YELLOW_600,
    },
    total: {
        flexShrink: 1,
        color: ColorEnum.YELLOW_600,
        textAlign: 'right',
    },
})

export default Handover
